/**
 * @file     consentPayload.ts
 * @purpose  The consent fields a form submits from ConsentChoices: the ticked
 *           boxes with their versions, plus the locale and page they were shown
 *           in, which the server needs to rebuild the exact text for the ledger.
 */
import { emailConsentPayload } from '@/core/config/emailConsent';
import { CONTACT_CONSENT_VERSION, smsConsentPayload } from '@/core/config/smsConsent';

export type ConsentValues = { email: boolean; sms: boolean };

export const EMPTY_CONSENT: ConsentValues = { email: false, sms: false };

export type ConsentPayload = {
  smsOptIn?: true;
  smsConsentVersion?: string;
  emailOptIn?: true;
  emailConsentVersion?: string;
  consentLocale?: string;
  consentPage?: string;
};

function currentPage(): string | undefined {
  return typeof window === 'undefined' ? undefined : window.location.pathname;
}

/**
 * Merge both boxes into one payload. The SMS box only counts with a phone; an
 * empty box adds nothing, so a form with both boxes empty submits no consent.
 * `combined` means the form showed the single email-or-text box, so both
 * channels cite its version.
 */
export function consentChoicesPayload(
  values: ConsentValues,
  phone: unknown,
  context: { locale?: string; page?: string; combined?: boolean } = {},
): ConsentPayload {
  const boxes: ConsentPayload = { ...smsConsentPayload(phone, values.sms), ...emailConsentPayload(values.email) };
  if (Object.keys(boxes).length === 0) return {};
  if (context.combined) {
    if (boxes.smsOptIn) boxes.smsConsentVersion = CONTACT_CONSENT_VERSION;
    if (boxes.emailOptIn) boxes.emailConsentVersion = CONTACT_CONSENT_VERSION;
  }
  const page = context.page ?? currentPage();
  return {
    ...boxes,
    ...(context.locale ? { consentLocale: context.locale } : {}),
    ...(page ? { consentPage: page } : {}),
  };
}
